import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { get } from "../../services/smartApiService";
import analyticsService, { CompanyAnalytics } from "../../services/analyticsService";
import DecisionSummary from "./components/DecisionSummary";
import PricePerformanceSnapshot from "./components/PricePerformanceSnapshot";
import ValuationFundamentals from "./components/ValuationFundamentals";
import TechnicalIndicators from "./components/TechnicalIndicators";
import VolumeMomentum from "./components/VolumeMomentum";
import RiskSentiment from "./components/RiskSentiment";
import FetchPricesModal from "./components/FetchPricesModal";
import FinancialDataModal from "./components/FinancialDataModal";
import "./CompanyInformation.scss";

interface CompanyDetails {
  id: number;
  name: string;
  symbol: string;
  overall_rating: string;
  analyst_rating: string;
  one_year_return: number;
}

const CompanyInformation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [analytics, setAnalytics] = useState<CompanyAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFetchPricesModal, setShowFetchPricesModal] = useState<boolean>(false);
  const [showFinancialDataModal, setShowFinancialDataModal] = useState<boolean>(false);

  // Load company details and analytics
  useEffect(() => {
    if (id) {
      loadCompanyData();
    }
  }, [id]);

  const loadCompanyData = () => {
    setLoading(true);
    setError(null);

    // Load company details
    get(`/api/companies/${id}/details`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setCompany(response.data.data || null);
      },
      error: (err) => {
        console.error("Failed to load company details:", err);
        setError("Failed to load company details");
      },
    });

    // Load analytics data
    if (id) {
      analyticsService.getAllAnalytics(parseInt(id)).subscribe({
        next: (response: any) => {
          setAnalytics(response.data || null);
          setLoading(false);
        },
        error: (err) => {
          console.error("Failed to load analytics:", err);
          // Don't set error here - analytics might not be calculated yet
          setLoading(false);
        },
      });
    }
  };

  const handleRefresh = () => {
    if (!id) return;

    setRefreshing(true);

    analyticsService.refreshAnalytics(parseInt(id), false).subscribe({
      next: (response: any) => {
        console.log("Analytics refreshed:", response.data);

        // Reload analytics after refresh
        setTimeout(() => {
          analyticsService.getAllAnalytics(parseInt(id!)).subscribe({
            next: (analyticsResponse: any) => {
              setAnalytics(analyticsResponse.data || null);
              setRefreshing(false);
            },
            error: (err) => {
              console.error("Failed to reload analytics:", err);
              setRefreshing(false);
            },
          });
        }, 1000); // Wait 1 second for calculations to complete
      },
      error: (err) => {
        console.error("Failed to refresh analytics:", err);
        setRefreshing(false);
      },
    });
  };

  const handleFetchPricesSuccess = () => {
    setShowFetchPricesModal(false);
    // Reload analytics after fetching prices
    if (id) {
      setTimeout(() => {
        analyticsService.getAllAnalytics(parseInt(id)).subscribe({
          next: (analyticsResponse: any) => {
            setAnalytics(analyticsResponse.data || null);
          },
          error: (err) => {
            console.error("Failed to reload analytics:", err);
          },
        });
      }, 1000);
    }
  };

  const handleFinancialDataSuccess = () => {
    setShowFinancialDataModal(false);
    // Reload analytics after submitting financial data
    if (id) {
      setTimeout(() => {
        analyticsService.getAllAnalytics(parseInt(id)).subscribe({
          next: (analyticsResponse: any) => {
            setAnalytics(analyticsResponse.data || null);
          },
          error: (err) => {
            console.error("Failed to reload analytics:", err);
          },
        });
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="company-information">
        <div className="container">
          <div className="has-text-centered" style={{ padding: "3rem" }}>
            <div className="loader is-loading" style={{ height: "100px" }}></div>
            <p className="mt-4">Loading company information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="company-information">
        <div className="container">
          <button className="button is-light mb-4" onClick={() => navigate("/dashboard/companies")}>
            <span className="icon">
              <i className="fas fa-arrow-left"></i>
            </span>
            <span>Back to Companies</span>
          </button>
          <div className="notification is-danger">
            {error || "Company not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-information" style={{ padding: '1rem' }}>
      <div className="container is-fluid">
        {/* Compact Header */}
        <div className="level mb-3" style={{ backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: '4px' }}>
          <div className="level-left">
            <div className="level-item">
              <button className="button is-small is-light mr-3" onClick={() => navigate("/dashboard/companies")}>
                <span className="icon is-small">
                  <i className="fas fa-arrow-left"></i>
                </span>
              </button>
              <div>
                <h1 className="title is-5 mb-0">{company.name}</h1>
                <p className="is-size-7 has-text-grey mt-1">
                  <span className="tag is-info is-small">{company.symbol}</span>
                  {analytics?.last_updated?.performance && (
                    <span className="ml-2">
                      <i className="fas fa-clock"></i> {new Date(analytics.last_updated.performance).toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <div className="buttons">
                <button
                  className={`button is-small is-primary ${refreshing ? 'is-loading' : ''}`}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh analytics"
                >
                  <span className="icon is-small">
                    <i className="fas fa-sync-alt"></i>
                  </span>
                  <span>Refresh</span>
                </button>
                <button
                  className="button is-small is-info"
                  onClick={() => setShowFetchPricesModal(true)}
                  title="Fetch prices"
                >
                  <span className="icon is-small">
                    <i className="fas fa-download"></i>
                  </span>
                  <span>Prices</span>
                </button>
                <button
                  className="button is-small is-success"
                  onClick={() => setShowFinancialDataModal(true)}
                  title="Add financials"
                >
                  <span className="icon is-small">
                    <i className="fas fa-file-invoice-dollar"></i>
                  </span>
                  <span>Financials</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {!analytics && (
          <div className="notification is-warning is-light py-2 px-3 mb-2">
            <p className="is-size-7 mb-0">
              <strong>Analytics not calculated.</strong> Click "Refresh" to calculate now.
            </p>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="columns" style={{ gap: '0.5rem' }}>
          {/* Left Column - Main Analytics */}
          <div className="column is-8" style={{ padding: '0 0.25rem' }}>
            <PricePerformanceSnapshot
              companyId={parseInt(id!)}
              analytics={analytics}
              onRefresh={loadCompanyData}
            />

            <ValuationFundamentals
              companyId={parseInt(id!)}
              company={company}
              analytics={analytics}
              onRefresh={loadCompanyData}
            />

            <TechnicalIndicators
              companyId={parseInt(id!)}
              analytics={analytics}
              onRefresh={loadCompanyData}
            />

            <VolumeMomentum
              companyId={parseInt(id!)}
              analytics={analytics}
              onRefresh={loadCompanyData}
            />
          </div>

          {/* Right Column - Decision Summary & Risk */}
          <div className="column is-4" style={{ padding: '0 0.25rem' }}>
            <DecisionSummary companyId={parseInt(id!)} analytics={analytics} />

            <RiskSentiment
              companyId={parseInt(id!)}
              company={company}
              analytics={analytics}
              onRefresh={loadCompanyData}
            />
          </div>
        </div>
      </div>

      <FetchPricesModal
        isOpen={showFetchPricesModal}
        onClose={() => setShowFetchPricesModal(false)}
        onSuccess={handleFetchPricesSuccess}
        symbol={company.symbol}
      />

      <FinancialDataModal
        isOpen={showFinancialDataModal}
        onClose={() => setShowFinancialDataModal(false)}
        onSuccess={handleFinancialDataSuccess}
        companyId={parseInt(id!)}
        companyName={company.name}
        companySymbol={company.symbol}
      />
    </div>
  );
};

export default CompanyInformation;
